/**
 * nebulaArtifactDiff.js
 *
 * Diffs two HTML artifact strings line-by-line using the `diff` package (LCS).
 * Returns an array of annotated line objects suitable for rendering a unified
 * diff inside the Nebula canvas.
 *
 * Each returned object:
 *   { type: 'added' | 'removed' | 'unchanged' | 'collapsed', value: string, count?: number }
 *
 * `collapsed` entries represent runs of unchanged lines that have been folded
 * down to a single summary row ("↕ N unchanged lines").
 */

import { diffLines } from "diff";

const CONTEXT_LINES = 3; // unchanged lines to show on each side of a change

/**
 * @param {string} oldHtml — previous artifact HTML
 * @param {string} newHtml — current artifact HTML
 * @returns {{ type: 'added'|'removed'|'unchanged'|'collapsed', value: string, count?: number }[]}
 */
export function diffArtifacts(oldHtml, newHtml) {
  if (!oldHtml || !newHtml) return [];

  const changes = diffLines(oldHtml, newHtml, { newlineIsToken: false });

  // Flatten to individual annotated lines first
  const flat = [];
  for (const change of changes) {
    const lines = change.value.replace(/\n$/, "").split("\n");
    const type = change.added ? "added" : change.removed ? "removed" : "unchanged";
    for (const line of lines) {
      flat.push({ type, value: line });
    }
  }

  // Collect indices of changed lines
  const changedSet = new Set();
  flat.forEach((l, i) => { if (l.type !== "unchanged") changedSet.add(i); });

  // If nothing changed, return empty
  if (changedSet.size === 0) return [];

  // Build the set of visible line indices (changed ± CONTEXT_LINES)
  const visible = new Set();
  for (const idx of changedSet) {
    for (let d = -CONTEXT_LINES; d <= CONTEXT_LINES; d++) {
      const j = idx + d;
      if (j >= 0 && j < flat.length) visible.add(j);
    }
  }

  // Build output, inserting `collapsed` sentinels for hidden runs
  const result = [];
  let i = 0;
  while (i < flat.length) {
    if (visible.has(i)) {
      result.push(flat[i]);
      i++;
    } else {
      // Count the run of hidden lines
      let start = i;
      while (i < flat.length && !visible.has(i)) i++;
      const count = i - start;
      result.push({ type: "collapsed", value: `↕  ${count} unchanged line${count === 1 ? "" : "s"}`, count });
    }
  }

  return result;
}

/**
 * Returns true if there are any meaningful differences between two HTML strings.
 * Cheap check — use before computing the full diff.
 */
export function hasDiff(oldHtml, newHtml) {
  return !!oldHtml && !!newHtml && oldHtml !== newHtml;
}

/**
 * Maps the output of `diffArtifacts()` to 1-based line numbers in the NEW file
 * so CodeMirror decorations can be applied to the correct lines.
 *
 * @param {Array} diffLines  Output of diffArtifacts()
 * @returns {{ addedLineNos: number[], removedLineNos: number[], ghostsByLineNo: Map<number, string[]> }}
 *   addedLineNos    — lines that exist in new file and were added/changed
 *   removedLineNos  — empty (kept for backward compat; deletions are shown as ghosts)
 *   ghostsByLineNo  — Map of newLineNo → array of removed-line strings to render
 *                     as red strikethrough "ghost" widgets ABOVE that line.
 *                     Deletions at end-of-file are keyed under (lastLine+1).
 */
export function getDiffLineMap(diffLines) {
  const addedLineNos = [];
  const ghostsByLineNo = new Map();
  let newLineNo = 1;
  let pendingRemoved = [];

  const flushPending = (atLine) => {
    if (pendingRemoved.length === 0) return;
    const existing = ghostsByLineNo.get(atLine) || [];
    ghostsByLineNo.set(atLine, [...existing, ...pendingRemoved]);
    pendingRemoved = [];
  };

  for (const entry of diffLines) {
    if (entry.type === "collapsed") {
      flushPending(newLineNo);
      newLineNo += entry.count ?? 0;
    } else if (entry.type === "removed") {
      pendingRemoved.push(entry.value);
      // newLineNo doesn't advance — removed lines don't exist in the new file
    } else if (entry.type === "added") {
      flushPending(newLineNo);
      addedLineNos.push(newLineNo);
      newLineNo++;
    } else if (entry.type === "unchanged") {
      flushPending(newLineNo);
      newLineNo++;
    }
  }
  // End-of-file deletions
  flushPending(newLineNo);

  return { addedLineNos, removedLineNos: [], ghostsByLineNo };
}
